import type { Location } from 'react-router-dom'
import type { RouteConfig, RouteMetadata } from '../app/routes'
import { routes } from '../app/routes'

interface UseRouteMetadataParams {
    /** Location object from router (required for dependency injection) */
    location: Location
}

/**
 * Hook to get metadata for the current route.
 * Returns the route metadata if available, or null if not found.
 *
 * Route matching logic:
 * - Exact match for root path '/'
 * - Exact match for other paths
 * - For nested routes like '/review/personal', matches the most specific route first
 */
export function useRouteMetadata({ location }: UseRouteMetadataParams): RouteMetadata | null {
    const pathname = location.pathname

    // Sort routes by path length (longest first) to match most specific routes first
    // This ensures '/review/personal' matches before '/review'
    const sortedRoutes = [...routes].sort((a, b) => b.path.length - a.path.length)

    // Find the route that matches the current pathname
    const route = sortedRoutes.find((r: RouteConfig) => {
        // Never match the catch-all wildcard via the normal branches; it is
        // handled explicitly as a fallback below so known paths always win.
        if (r.path === '*') {
            return false
        }
        // Exact match for root
        if (r.path === '/' && pathname === '/') {
            return true
        }
        // Exact match for other paths
        if (r.path !== '/' && pathname === r.path) {
            return true
        }
        // For nested routes, check if pathname starts with the route path followed by '/'
        // This handles cases like '/review/personal' matching '/review/personal' exactly
        if (r.path !== '/' && pathname.startsWith(r.path + '/')) {
            return true
        }
        return false
    })

    // A known path wins; otherwise fall back to the catch-all '*' route's
    // metadata so 404 pages get their own SEO title/description.
    if (route) {
        return route.metadata || null
    }

    const wildcard = routes.find((r: RouteConfig) => r.path === '*')
    return wildcard?.metadata ?? null
}
